/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled, { css, SimpleInterpolation } from "styled-components";
import * as React from "react";
import { ConditionalWrapper } from "components/shared/ConditionalWrapper";
import { WithDottedBorder } from "components/shared/DottedBorder";
import { DeleteButton } from "components/EnhancedSpeech/ProTierEnhanceSpeech/DeleteButton";
import {
  TrackItemStatus,
  TrackItemStatusProps,
} from "components/EnhancedSpeech/ProTierEnhanceSpeech/TrackItem/TrackItemStatus";
import enhanceFailedIconUrl from "./TrackItem/images/enhance-failed.svg";
import { fontFamilies } from "components/shared/Styles";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";
import { ModelVersionBadgeListItem } from "components/EnhancedSpeech/ProTierEnhanceSpeech/ModelVersionBadge";

export interface TrackItemProps {
  id: string;
  trackName: string;
  modelVersion: ModelVersion;
  onSelect: VoidFunction;
  isSelected: boolean;
  isFinished: boolean;
  isProcessing: boolean;
  isUploading: boolean;
  uploadPercent: number;
  statusProps: TrackItemStatusProps;

  isDeleting: boolean;
  remove: VoidFunction;
}

export const TrackItem: React.FC<TrackItemProps> = ({
  trackName,
  modelVersion,
  onSelect: select,
  isSelected,
  isFinished,
  isProcessing,
  isUploading,
  uploadPercent,
  statusProps,
  isDeleting,
  remove,
}) => {
  //TODO: Re-evaluate all these states?
  const isFailed = Boolean(statusProps.errorType);
  const isBusy = !isFinished || isFailed;
  return (
    <ConditionalWrapper
      condition={isBusy}
      wrapperComponent={WithDottedBorder}
      wrapperProps={{ color: "#e6e6e6", dashlength: "5px" }}
    >
      <TrackItemContainer
        className="track-item" // used for identifying in test
        processing={isBusy}
        selected={isSelected}
        onClick={select}
      >
        <TrackItemInfo selected={isSelected}>
          <TitleGroup>
            <TrackItemTitle title={trackName}>{trackName}</TrackItemTitle>
            <ModelVersionBadgeListItem selected={isSelected}>
              {modelVersion}
            </ModelVersionBadgeListItem>
          </TitleGroup>
          <TrackItemStatus {...statusProps} />
        </TrackItemInfo>
        <TrackItemActionRow>
          {(isProcessing || isUploading) && (
            <overlay-trigger disabled={!isUploading || undefined}>
              <sp-progress-circle
                size="m"
                indeterminate={!isUploading || uploadPercent === 0 || undefined}
                progress={
                  (uploadPercent != 0 && uploadPercent * 100) || undefined
                }
                aria-label={I18n.t("loading")}
                slot="trigger"
              />
              <sp-tooltip slot="hover-content">
                <span>{`${Math.floor(uploadPercent * 100)}%`}</span>
              </sp-tooltip>
            </overlay-trigger>
          )}
          {isFailed && (
            <img
              src={enhanceFailedIconUrl}
              alt={I18n.t("enhance_speech.premium.status.failed")}
            />
          )}
          {!isUploading && (
            <DeleteButton performDelete={remove} isDeleting={isDeleting} />
          )}
        </TrackItemActionRow>
      </TrackItemContainer>
    </ConditionalWrapper>
  );
};

const TrackItemContainer = styled.div<{
  processing: boolean;
  selected: boolean;
}>`
  padding: 24px;
  border-radius: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  ${({ processing }): SimpleInterpolation => {
    if (!processing) {
      return css`
        border: 3px solid #e6e6e6;
        &:hover {
          border-color: var(--spectrum-indigo-900);
        }
      `;
    } else {
      return css`
        border: 3px solid transparent;
      `;
    }
  }}

  ${({ selected }): SimpleInterpolation => {
    if (selected) {
      return css`
        background: var(--spectrum-global-color-static-indigo-600);
        border-color: var(--spectrum-global-color-static-indigo-600);
        --system-spectrum-actionbutton-content-color-default: white;
        --system-spectrum-actionbutton-content-color-hover: white;
        --system-spectrum-actionbutton-content-color-down: white;
        --system-spectrum-actionbutton-background-color-default: rgba(
          0,
          0,
          0,
          0.1
        );
        --system-spectrum-actionbutton-background-color-hover: rgba(
          0,
          0,
          0,
          0.25
        );
        --system-spectrum-actionbutton-background-color-down: rgba(
          0,
          0,
          0,
          0.35
        );

        img {
          filter: invert(
            100%
          ); // Used to invert the color to white of the delete icon
        }
      `;
    }
  }}
`;

const TrackItemInfo = styled.div<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 2;
  max-width: 90%;
  align-items: start;
  overflow: hidden;
  color: ${({ selected }): string => (selected ? "white" : "#222")};
  gap: 2px;
`;

const TrackItemTitle = styled.span`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  font: normal normal bold 16px/20px ${fontFamilies};
`;

const TrackItemActionRow = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 16px;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: flex-start;
  align-items: start;
  max-width: inherit;
`;
